import { Button } from 'components/Button';
import { DropdownSingleSelect } from 'components/Dropdown/DropdownVariants/DropdownSingleSelect';
import { DropdownOptionProps } from 'components/Dropdown/helper/DropdownTypes';
import { CountryProps } from 'features/Dashboard/Map/DashboardMap';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCRMMandates } from '../../../../hooks/queries';
import { marketSegments } from '../../../../utils/marketSegments.utils';
import { CPLinkProps } from '../../../../utils/principal-service/customer-portal-insights-endpoints.types';
import { Principal } from '../../../../utils/types';

export type MarketSelectorConfig = {
  country: string | null;
  marketSegmentId: string | null;
  language: string | null;
};
type MarketSelectorProps = {
  supplierPageOptionData?: CPLinkProps[];
  currentPrincipal: Principal;
  currentConfigState: MarketSelectorConfig | undefined;
  onSelect: (value: MarketSelectorConfig) => void;
};

export const iso3CountryCodesToDropdownOptions = (
  input: string[],
  countriesFromMandates: CountryProps[],
): DropdownOptionProps[] =>
  input
    .map((iso3CountryCode) => {
      const countryName = countriesFromMandates.find(
        ({ ISO_A3 }) => ISO_A3 === iso3CountryCode,
      )?.name;

      if (!countryName) return undefined;

      return {
        name: countryName,
        value: iso3CountryCode,
      };
    })
    .filter((i) => Boolean(i)) as DropdownOptionProps[];

export const MarketSelector: React.FC<MarketSelectorProps> = ({
  supplierPageOptionData,
  currentConfigState,
  onSelect,
  currentPrincipal,
}) => {
  const { t } = useTranslation();

  const [selectedConfigData, setSelectedConfigData] =
    useState<MarketSelectorConfig>(
      currentConfigState || {
        country: null,
        marketSegmentId: null,
        language: null,
      },
    );

  const { data: mandates } = useCRMMandates({
    principalPri: currentPrincipal.id,
  });
  const countriesfromMandates =
    mandates?.countries.map((country) => ({
      ISO_A3: country.iso,
      name: country.name,
    })) ?? [];

  const countryOptions =
    iso3CountryCodesToDropdownOptions(
      [...new Set(supplierPageOptionData?.map((i) => i.country))],
      countriesfromMandates,
    ) || [];

  const marketSegmentOptions = [
    ...new Set(
      supplierPageOptionData
        ?.filter((i) => i.country === selectedConfigData?.country)
        .map((i) => i.market_segment_code) || [],
    ),
  ].map((option) => ({
    name: marketSegments[option]?.name,
    value: option,
  }));

  const languageOptions = [
    ...new Set(
      supplierPageOptionData
        ?.filter(
          (i) =>
            i.country === selectedConfigData?.country &&
            i.market_segment_code === selectedConfigData.marketSegmentId,
        )
        .map((i) => i.language) || [],
    ),
  ].map((languageCode) => ({
    name: t(`languages:${languageCode}`),
    value: languageCode,
  }));

  const handleDropdownChange = (
    field: keyof MarketSelectorConfig,
    selectedOption: string,
  ) => {
    if (selectedConfigData?.[field] === selectedOption) {
      return;
    }

    setSelectedConfigData(({ country, marketSegmentId }) => {
      switch (field) {
        case 'country':
          return {
            country: selectedOption,
            marketSegmentId: null,
            language: null,
          };
        case 'marketSegmentId':
          return { country, marketSegmentId: selectedOption, language: null };
        case 'language':
          return { country, marketSegmentId, language: selectedOption };
      }
    });
  };

  const onConfirm = () => {
    if (!selectedConfigData) {
      return;
    }
    onSelect(selectedConfigData);
  };

  return (
    <div className="text-grey-700 flex w-full items-end gap-3 pb-6 pt-3">
      <div className="w-72">
        <div className="mb-1 font-semibold">
          {t(
            'features:insights:customer-portal-insights:my-supplier-page:marketSelectorlabels:country',
          )}
        </div>
        <DropdownSingleSelect
          placeholder={t('placeholders:dropdownDefault')}
          options={countryOptions}
          onChange={(option) => {
            handleDropdownChange('country', option.value.toString());
          }}
        />
      </div>

      <div className="w-72">
        <div className="mb-1 font-semibold">
          {t(
            'features:insights:customer-portal-insights:my-supplier-page:marketSelectorlabels:marketSegment',
          )}
        </div>
        <DropdownSingleSelect
          disabled={!selectedConfigData?.country}
          placeholder={t('placeholders:dropdownDefault')}
          options={marketSegmentOptions}
          onChange={(option) => {
            handleDropdownChange('marketSegmentId', option.value.toString());
          }}
        />
      </div>
      <div className="w-72">
        <div className="mb-1 font-semibold">
          {t(
            'features:insights:customer-portal-insights:my-supplier-page:marketSelectorlabels:language',
          )}
        </div>
        <DropdownSingleSelect
          disabled={!selectedConfigData?.marketSegmentId}
          placeholder={t('placeholders:dropdownDefault')}
          options={languageOptions}
          onChange={(option) => {
            handleDropdownChange('language', option.value.toString());
          }}
        />
      </div>
      <Button
        className="h-7 w-36 items-center"
        label="Select"
        onClick={onConfirm}
        disabled={
          !selectedConfigData?.country ||
          !selectedConfigData?.marketSegmentId ||
          !selectedConfigData?.language
        }
      />
    </div>
  );
};
